import {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  useRef,
  type ReactNode,
} from 'react'
import { api } from '../services/api'

const PLAN_ACTION_KEY = 'autodiag:planaction-seen'
const ATTESTATION_KEY = 'autodiag:attestation-seen'

interface NotificationsContextType {
  attestationsPending: number
  planActionNew: number
  refreshNotifications: () => Promise<void>
  acknowledgePlanAction: () => void
  acknowledgeAttestation: () => void
}

const NotificationsContext = createContext<NotificationsContextType | null>(null)

function getStoredIds(key: string): Set<string> {
  try {
    const raw = localStorage.getItem(key)
    if (!raw) return new Set()
    return new Set(JSON.parse(raw) as string[])
  } catch {
    return new Set()
  }
}

function storeIds(key: string, ids: Set<string>): void {
  localStorage.setItem(key, JSON.stringify([...ids]))
}

/** Calcule le badge : IDs de l'API moins IDs deja vus dans localStorage */
function computeBadge(key: string, apiIds: string[], pendingAck: boolean): { count: number } {
  if (pendingAck) {
    // L'utilisateur a visite la page avant que l'API reponde —
    // marquer tout comme vu
    storeIds(key, new Set(apiIds))
    return { count: 0 }
  }

  const seen = getStoredIds(key)
  // Nettoyer les IDs qui n'existent plus dans l'API
  const validSeen = new Set([...seen].filter(id => apiIds.includes(id)))
  if (validSeen.size !== seen.size) {
    storeIds(key, validSeen)
  }
  const count = apiIds.filter(id => !validSeen.has(id)).length
  return { count }
}

export function NotificationsProvider({ children }: { children: ReactNode }) {
  const [attestationsPending, setAttestationsPending] = useState(0)
  const [planActionNew, setPlanActionNew] = useState(0)
  // Refs pour gerer la race condition : acknowledge appele avant que l'API reponde
  const pendingAckPlanAction = useRef(false)
  const pendingAckAttestation = useRef(false)
  // Derniers IDs connus (pour stocker en localStorage immediatement lors d'un acknowledge)
  const lastPlanIds = useRef<string[]>([])
  const lastAttestationIds = useRef<string[]>([])

  const refreshNotifications = useCallback(async () => {
    try {
      const data = await api.getNotifications()

      lastPlanIds.current = data.completedDiagnosticIds
      const plan = computeBadge(PLAN_ACTION_KEY, data.completedDiagnosticIds, pendingAckPlanAction.current)
      setPlanActionNew(plan.count)
      if (pendingAckPlanAction.current) pendingAckPlanAction.current = false

      lastAttestationIds.current = data.attestationPendingIds
      const att = computeBadge(ATTESTATION_KEY, data.attestationPendingIds, pendingAckAttestation.current)
      setAttestationsPending(att.count)
      if (pendingAckAttestation.current) pendingAckAttestation.current = false
    } catch {
      // Silently fail — badges are optional
    }
  }, [])

  const acknowledgePlanAction = useCallback(() => {
    if (lastPlanIds.current.length > 0) {
      storeIds(PLAN_ACTION_KEY, new Set(lastPlanIds.current))
    } else {
      pendingAckPlanAction.current = true
    }
    setPlanActionNew(0)
  }, [])

  const acknowledgeAttestation = useCallback(() => {
    if (lastAttestationIds.current.length > 0) {
      storeIds(ATTESTATION_KEY, new Set(lastAttestationIds.current))
    } else {
      pendingAckAttestation.current = true
    }
    setAttestationsPending(0)
  }, [])

  useEffect(() => {
    refreshNotifications()
  }, [refreshNotifications])

  return (
    <NotificationsContext.Provider
      value={{ attestationsPending, planActionNew, refreshNotifications, acknowledgePlanAction, acknowledgeAttestation }}
    >
      {children}
    </NotificationsContext.Provider>
  )
}

/**
 * Hook pour consommer les notifications.
 * Retourne des valeurs par defaut si utilise hors du provider (pages tunnel).
 */
export function useNotifications(): NotificationsContextType {
  const context = useContext(NotificationsContext)
  if (!context) {
    return {
      attestationsPending: 0,
      planActionNew: 0,
      refreshNotifications: async () => {},
      acknowledgePlanAction: () => {},
      acknowledgeAttestation: () => {},
    }
  }
  return context
}
